import type {
  HoursRange,
  OperatingHoursOutput,
} from "@/api/catalog-service/model";

type CandidateRange = {
  range: HoursRange;
  day: Date;
};

const MINUTES_PER_HOUR = 60;
const WEEKDAYS: Array<keyof OperatingHoursOutput> = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export function isWithinOperatingHours(
  hours: OperatingHoursOutput | null | undefined,
  now = new Date(),
): boolean {
  if (!hours) return true;

  for (const candidate of candidateRanges(hours, now)) {
    if (contains(candidate.range, candidate.day, now)) return true;
  }
  return false;
}

export function durationUntilNextMenuBoundary(
  allHours: Iterable<OperatingHoursOutput | null | undefined>,
  now = new Date(),
): number | null {
  let next: Date | null = null;

  for (const hours of allHours) {
    if (!hours) continue;
    for (let dayOffset = 0; dayOffset < 8; dayOffset += 1) {
      const day = startOfDay(addDays(now, dayOffset));
      for (const range of rangesForDay(hours, day)) {
        for (const boundary of boundariesFor(range, day)) {
          if (boundary > now && (!next || boundary < next)) {
            next = boundary;
          }
        }
      }
    }
  }

  return next ? Math.max(0, next.getTime() - now.getTime()) : null;
}

function candidateRanges(
  hours: OperatingHoursOutput,
  now: Date,
): CandidateRange[] {
  const today = startOfDay(now);
  const yesterday = addDays(today, -1);
  return [
    ...rangesForDay(hours, today).map((range) => ({ range, day: today })),
    ...rangesForDay(hours, yesterday)
      .filter(isOvernight)
      .map((range) => ({ range, day: yesterday })),
  ];
}

function contains(range: HoursRange, day: Date, now: Date): boolean {
  const open = timeOnDay(day, range.open);
  let close = timeOnDay(day, range.close);
  if (close <= open) close = addDays(close, 1);
  return now >= open && now < close;
}

function boundariesFor(range: HoursRange, day: Date): Date[] {
  const open = timeOnDay(day, range.open);
  let close = timeOnDay(day, range.close);
  if (close <= open) close = addDays(close, 1);
  return [open, close];
}

function rangesForDay(hours: OperatingHoursOutput, day: Date): HoursRange[] {
  return hours[WEEKDAYS[day.getDay()]]?.RootModel ?? [];
}

function isOvernight(range: HoursRange): boolean {
  return parseTime(range.close) <= parseTime(range.open);
}

function timeOnDay(day: Date, value: string): Date {
  const minutes = parseTime(value);
  const d = startOfDay(day);
  d.setHours(Math.floor(minutes / MINUTES_PER_HOUR), minutes % MINUTES_PER_HOUR);
  return d;
}

function parseTime(value: string): number {
  const [rawHour, rawMinute] = value.split(":");
  const hour = Number.parseInt(rawHour ?? "0", 10);
  const minute = Number.parseInt(rawMinute ?? "0", 10);
  return (
    (Number.isFinite(hour) ? hour : 0) * MINUTES_PER_HOUR +
    (Number.isFinite(minute) ? minute : 0)
  );
}

function startOfDay(value: Date): Date {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    0,
    0,
    0,
    0,
  );
}

function addDays(value: Date, days: number): Date {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate() + days,
    value.getHours(),
    value.getMinutes(),
    value.getSeconds(),
    value.getMilliseconds(),
  );
}
